import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from 'react-dom';
import { LassieContext } from "../../../context/LassieProvider";
import { lightningScenarioAPI } from "../../../api";
import OptionsMenu from "../../../components/options-menu/OptionsMenu";
import WplSwitch from "../../../components/wpl-switch/wplSwitch";
import AlertPopup from "../../../components/alert-popup/AlertPopup";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import editIconGray from '../../../assets/img/edit-gray.png';
import copySettingsIconGray from '../../../assets/img/copy-settings-gray.png';
import optionIconWhite from "../../../assets/img/option-icon-white.png";
import "./lightning-notifications-settings.css";

export default function LightningNotificationsSettings() {
  const navigate = useNavigate();
  const {
    windfarms,
    notificationsOverview,
    settingsLoading,
    fetchScenariosOverview,
    updateScenarioStatus,
    notificationUpdate,
  } = useContext(LassieContext);

  const wfRef = useRef({});
  const [updatingWfIds, setUpdatingWfIds] = useState([]);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showOptions, setShowOptions] = useState(null);
  const [copySettings, setCopySettings] = useState({
    is_active: false,
    source_id: null,
    target_wf_ids: []
  });
  const [notificationPopup, setNotificationPopup] = useState({
    show: false,
    status: '',
    title: '',
    buttonText: '',
    buttonStatus: '',
    text: '',
    action: null,
    buttonText2: '',
    buttonStatus2: '',
    action2: null,
  })

  useEffect(() => {
    fetchScenariosOverview();
  }, []);

  const disclaimerText = () => {
    return (
      <div>
        Before proceeding, please confirm that you understand the
        implications of adjusting these settings, as they directly influence
        the notifications and triggers related to windfarm activities.
        Changes are not recommended unless you fully understand their
        impact.
        <p className="disclaimer-text">
          For more information, visit our
          <Link to="/lassie/support" className="faq-link">
            FAQ
          </Link>
          to learn how these settings work.
        </p>
      </div>)
  }

  const copyConfirmText = () => {
    return (
      <div>
        <p>Are you sure you want to copy notification settings from <strong>{getWindfarmNameById(copySettings.source_id)}</strong> to the following windfarms?</p>
        <ul className="target-windfarms-list">
          {copySettings.target_wf_ids.map(id => (
            <li key={id}><strong>{getWindfarmNameById(id)}</strong></li>
          ))}
        </ul>
        <p className="warning-text">
          This will replace all existing notification settings in the target windfarms and cannot be undone.
        </p>
      </div>
    )
  }

  const handleEditSettings = (windfarm) => {
    setNotificationPopup({
      show: true,
      status: 'warning',
      title: 'Disclaimer',
      buttonText: 'Cancel',
      buttonStatus: 'wrong',
      action: () => {
        setNotificationPopup({ show: false });
      },
      buttonText2: 'Proceed',
      buttonStatus2: 'success',
      action2: () => navigate(`windfarm/${windfarm.id}`),
      text: disclaimerText(),
    });
  };

  const copyNotificationSettings = async (source_wf_id, target_wf_ids) => {
    setUpdatingWfIds([source_wf_id, ...target_wf_ids]);
    const payload = {};
    target_wf_ids.forEach(targetId => {
      payload[targetId] = notificationsOverview[targetId]?.map(n => n.scenario_id) || [];
    });

    try {
      await lightningScenarioAPI.copyWindfarmSettings(source_wf_id, payload);
      closeCopySettings();
      await fetchScenariosOverview();
    } catch (error) {
      console.error(`Error copying notification settings:`, error);
      setNotificationPopup({
        show: true,
        status: 'wrong',
        title: 'Something went wrong',
        buttonText: 'Close',
        buttonStatus: 'wrong',
        text: error.response?.data
      });
    } finally {
      setUpdatingWfIds([]);
    }
  };

  const getWindfarmNameById = (wfId) => {
    const wf = windfarms.find(wf => wf.id === wfId);
    return wf ? wf.name : 'Unknown';
  };

  const closeCopySettings = () => {
    setCopySettings({ is_active: false, source_id: null, target_wf_ids: [] });
  };

  const handleCopySettingsConfirm = () => {
    setNotificationPopup({
      show: true,
      status: 'warning',
      title: 'Copy Notification Settings',
      buttonText: 'Cancel',
      buttonStatus: 'wrong',
      action: () => {
        setNotificationPopup({ show: false });
      },
      buttonText2: 'Confirm copy',
      buttonStatus2: 'success',
      action2: () => {
        copyNotificationSettings(copySettings.source_id, copySettings.target_wf_ids);
        setNotificationPopup({ show: false });
      },
      text: copyConfirmText(),
    })

  };

  const handleWindfarmClick = (wf) => {
    if (copySettings.is_active && wf.id !== copySettings.source_id) {
      setCopySettings((prev) => ({
        ...prev,
        target_wf_ids: prev.target_wf_ids.includes(wf.id)
          ? prev.target_wf_ids.filter((id) => id !== wf.id)
          : [...prev.target_wf_ids, wf.id],
      }));
    }
  };

  const handleOptionClick = (e, wf) => {
    e.stopPropagation();
    const icon = wfRef.current[wf.id];
    if (icon) {
      const rect = icon.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX + 15,
      });
      setShowOptions({ id: wf.id, name: wf.name });
    }
  };

  return (
    <div className="notifications-settings-container">
      <h4>Manage notifications</h4>
      {copySettings.is_active && (
        <div className="copy-settings-header">
          <p className='instruction-text'>Select windfarms to copy settings to...</p>
          <div className="copy-settings-actions">
            <button
              className='notification-btn cancel-btn'
              onClick={closeCopySettings}
              aria-label="Cancel copy settings"
            >
              X
            </button>
            <button
              className='notification-btn success-btn'
              disabled={!copySettings.target_wf_ids.length}
              onClick={handleCopySettingsConfirm}
              aria-label="Confirm copy settings"
            >
              ✔
            </button>
          </div>
        </div>
      )}

      {settingsLoading ? (
        <div className="overlay loading">
          <TurbineAnimation />
        </div>
      ) : windfarms?.length ? (
        <div className="cards-area">
          {windfarms.map((wf) => (
            <div
              className={`notification-card ${updatingWfIds.includes(wf.id) ? 'updating-row' : ''}`}
              key={wf.id}
            >
              <div
                className="wf-name-area"
                data-selected={wf.id === copySettings.source_id}
                data-copy-mode={copySettings.is_active}
                onClick={() => handleWindfarmClick(wf)}
              >
                <h5 className="windfarm-name">
                  {wf.name}
                  {copySettings.is_active && wf.id !== copySettings.source_id && (
                    <div className="copy-checkbox">
                      <input
                        type="checkbox"
                        checked={copySettings.target_wf_ids.includes(wf.id)}
                        readOnly
                        aria-label={`Select ${wf.name} for copy`}
                      />
                    </div>
                  )}
                </h5>

                <div
                  className="option-cell"
                  onClick={(e) => handleOptionClick(e, wf)}
                >
                  <img
                    src={optionIconWhite}
                    className="action-icon option-btn"
                    alt="Options menu"
                    ref={(el) => (wfRef.current[wf.id] = el)}
                  />
                </div>
              </div>

              <div className="notification-area">
                {notificationsOverview[wf.id]?.map((n, i) => (
                  <div
                    key={`${wf.id}-${n.scenario_id}`}
                    className={notificationUpdate.id === n.scenario_id ? 'notification updating-row' : 'notification'}
                  >
                    <p>{n.title}</p>
                    <WplSwitch
                      value={n.isSubscribed}
                      onChanged={() => updateScenarioStatus(n, wf.id)}
                      disabled={updatingWfIds.includes(wf.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='redirection-message-container'>
          <p>No windfarms available</p>
        </div>
      )}

      {showOptions && createPortal(
        <div
          className="options-container"
          style={{
            position: 'absolute',
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 9999,
          }}
        >
          <OptionsMenu
            options={[
              {
                label: 'Edit settings',
                onClick: () => {
                  handleEditSettings(windfarms.find(wf => wf.id === showOptions.id));
                  setShowOptions(null);
                },
                img: editIconGray
              },
              {
                label: 'Copy notification settings',
                onClick: () => {
                  setCopySettings({
                    is_active: true,
                    source_id: showOptions.id,
                    target_wf_ids: []
                  });
                  setShowOptions(null);
                },
                img: copySettingsIconGray
              },
            ]}
            onClose={() => setShowOptions(null)}
          />
        </div>,
        document.body
      )}

      <AlertPopup status={notificationPopup.status}
        title={notificationPopup.title}
        showPopup={notificationPopup.show}
        closePopup={() => setNotificationPopup({ show: false })}
        buttonText={notificationPopup.buttonText}
        buttonStatus={notificationPopup.buttonStatus}
        action={notificationPopup.action}
        buttonText2={notificationPopup.buttonText2}
        buttonStatus2={notificationPopup.buttonStatus2}
        action2={notificationPopup.action2}
      >
        {notificationPopup.text}
      </AlertPopup>

    </div>
  );
}